import React, { HTMLAttributes, useEffect, useRef, useState } from 'react';
import { Text } from '_types/Text';
import { CTAChevron } from 'assets/helper/icons'
import { useKeydownNavigation } from 'hooks/keyboardEvents/useKeydownNavigation';
import { useTabbedContainer } from 'contexts/components/TabbedContainerContext';

type Tab = {
  id: string;
  elements: {
    svg_icon?: Text;
    tab_title: Text;
  };
}

type DivWithoutClassName = Omit<HTMLAttributes<HTMLDivElement>, 'className'>;

interface TabsProps extends DivWithoutClassName {
  buttonType: 'default' | 'hero';
  className?: string;
  hasSliderIndicatorBar?: boolean;
  selectedServiceIndex?: number;
  setSelectedService?: (selectedServiceId: number) => void;
  sliderBarDirection?: 'horizontal' | 'vertical';
  tabbedContent?: any;
}

export const Tabs = ({
  buttonType = 'default',
  className = 'fr-tabs-controller',
  hasSliderIndicatorBar = false,
  sliderBarDirection = 'horizontal',
  ...rest
}: TabsProps) => {
  const {
    activeTabIndex,
    allContent,
    handleChangeActiveTab
  } = useTabbedContainer<Tab>();

  const tabListRef = useRef<HTMLDivElement>(null);
  const tabsRef = useRef<(HTMLButtonElement | null)[]>([]);
  const [indicatorIcon, setIndicatorIcon] = useState<string | undefined>('');
  const [indicatorTitle, setIndicatorTitle] = useState<string>('');
  const [prevTabsHeights, setPrevTabsHeights] = useState<number>(0);
  const [prevTabsWidths, setPrevTabsWidths] = useState<number>(0);
  const [tabHeight, setTabHeight] = useState<number>(0);
  const [tabWidth, setTabWidth] = useState<number>(0);

  const defaultLayout: boolean = buttonType === "default" && sliderBarDirection !== "vertical";

  const sliderIndicatorStyles = sliderBarDirection === 'vertical'
        ? {
            height: tabHeight,
            transform: `translateY(${prevTabsHeights}px)`,
          }
        : {
            width: tabWidth,
            transform: `translateX(${prevTabsWidths}px)`,
          };

  useEffect(() => {
    if (!tabListRef.current) return;

    const tabsNodes = tabListRef.current.querySelectorAll("[role=tab]") as
      | NodeListOf<HTMLButtonElement>
      | undefined;

    if (!tabsNodes) return;

    const tabsList = Array.from(tabsNodes);
    const tabsKeyboardEvent = useKeydownNavigation({ tabs: tabsList });

    tabsNodes.forEach(tab => {
      tab.addEventListener('keydown', tabsKeyboardEvent.onKeydown)
    });
  }, [tabListRef]);

  const handleWindowResize = () => {
    const activeTabRef = tabsRef.current[activeTabIndex];

    if (activeTabRef !== null) {
      const currentTabHeight = activeTabRef.scrollHeight,
            currentTabIcon = allContent[activeTabIndex].elements.svg_icon ? allContent[activeTabIndex].elements.svg_icon?.value : '',
            currentTabTitle = allContent[activeTabIndex].elements.tab_title.value,
            currentTabWidth = activeTabRef.scrollWidth;

      setIndicatorIcon(currentTabIcon);
      setIndicatorTitle(currentTabTitle);
      setTabHeight(currentTabHeight);
      setTabWidth(currentTabWidth);
    }

    let previousTabsHeights: number = 0,
        previousTabsWidths: number = 0;

    for (let i = activeTabIndex; i--;) {
      const indexTabRef = tabsRef.current[i];
      if (indexTabRef !== null) {
        previousTabsHeights += indexTabRef.scrollHeight;
        previousTabsWidths += indexTabRef.scrollWidth;
      }
    }

    setPrevTabsHeights(previousTabsHeights);
    setPrevTabsWidths(previousTabsWidths);
  }

  useEffect(() => {
    handleWindowResize();
  }, [activeTabIndex]);

  useEffect(() => {
    window.addEventListener('resize', handleWindowResize);
    return () => window.removeEventListener('resize', handleWindowResize);
  }, []);

  return (
    <div className={ className }>
      <div
        className={ `${className}__wrapper` }
        ref={ tabListRef }
        role="tablist"
        { ...rest }
      >
        { allContent.map(({ id, elements }, index) => {
          const tabTitle: string = elements.tab_title.value;

          const activeTab: boolean = activeTabIndex === index;
          const ariaControls = `tabpanel-${tabTitle.replaceAll(' ', '_')}-${id}`;

          const defaultType = `button--tab${activeTab ? ' button--tab--active' : ''}`;
          const heroType = `button--hero-tab${activeTab ? ' button--hero-tab--active' : ''}`;
          const classNameTypeClass = `${className}__tab${activeTab ? ` ${className}__tab--active` : ''}`;

          let typeClass: string;

          switch (buttonType) {
            case 'hero':
              typeClass = heroType;
              break;
            default:
              typeClass = defaultType;
              break;
          }

          return (
            <button
              aria-controls={ ariaControls }
              aria-selected={ activeTab }
              className={ `${typeClass} ${classNameTypeClass}` }
              id={ `tab-${tabTitle.replaceAll(' ', '_')}-${id}` }
              key={ id }
              onClick={ () => handleChangeActiveTab(index) }
              ref={ ref => (tabsRef.current[index] = ref) }
              role="tab"
              tabIndex={ activeTab ? 0 : -1 }
              type="button"
            >
                <span className={ `${className}__title`}>
                  { tabTitle }
                  { (sliderBarDirection === 'vertical' && activeTab) && <CTAChevron /> }
                </span>
            </button>
          )
        }) }

        { hasSliderIndicatorBar && (
          <div
            className={ `${className}__indicator-wrapper` }
            style={ sliderIndicatorStyles }
          >
            <div className={ `button--tab ${className}__indicator` }>
              { defaultLayout && indicatorIcon && (
                <div
                  className={ `${className}__icon` }
                  contentEditable="false"
                  dangerouslySetInnerHTML={{ __html: indicatorIcon }}
                />
              ) }

              <span className={ `${className}__indicator-text` }>
                { indicatorTitle }
                { sliderBarDirection === 'vertical' && <CTAChevron /> }
              </span>
            </div>
          </div>
        ) }
      </div>
    </div>
  )
}
